import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Check, ChevronDown, Eye, EyeOff, School } from 'lucide-react';
import { registerUser } from '../../services/auth';
import { TeacherRegisterForm } from '../../types';
import {
  LoadingSpinner,
  MobileContainer,
  MobilePageHeader,
  MobileCard,
  PageTransition,
} from '../../components';
import { PLAN_ENTITLEMENTS, SubscriptionPlanId } from '../../types/subscription';
import subscriptionService from '../../services/subscriptionService';
import ClassTechLogo from '../../components/brand/ClassTechLogo';

const REGISTRATION_PLANS: SubscriptionPlanId[] = ['trial'];
type PickerName = 'specialization' | 'qualifications';

const TeacherRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlanId>('trial');
  const [openPicker, setOpenPicker] = useState<PickerName | null>(
    'specialization'
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm<TeacherRegisterForm>({
    defaultValues: {
      specialization: [],
      qualifications: [],
      subjects: [],
      acceptTerms: false,
    },
  });

  const password = watch('password');
  const selectedSpecializations = watch('specialization') || [];
  const selectedQualifications = watch('qualifications') || [];

  const specializations = [
    'الرياضيات',
    'العلوم',
    'الفيزياء',
    'الكيمياء',
    'الأحياء',
    'اللغة العربية',
    'اللغة الإنجليزية',
    'التاريخ',
    'الجغرافيا',
    'التربية الإسلامية',
    'الحاسوب',
    'جميع المواد',
  ];

  const qualificationOptions = [
    'بكالوريوس تربية',
    'بكالوريوس علوم',
    'بكالوريوس آداب',
    'بكالوريوس هندسة',
    'ماجستير تربية',
    'ماجستير علوم',
    'دكتوراه تربية',
    'دكتوراه علوم',
    'دبلوم تربوي',
    'شهادة معلم',
  ];

  const getPasswordStrength = (pwd: string) => {
    if (!pwd) return { strength: 0, text: 'ضعيفة جداً', color: 'bg-red-500' };

    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;

    switch (score) {
      case 0:
      case 1:
        return { strength: 20, text: 'ضعيفة جداً', color: 'bg-red-500' };
      case 2:
        return { strength: 40, text: 'ضعيفة', color: 'bg-orange-500' };
      case 3:
        return { strength: 60, text: 'متوسطة', color: 'bg-yellow-500' };
      case 4:
        return { strength: 80, text: 'قوية', color: 'bg-primary-600' };
      case 5:
        return { strength: 100, text: 'قوية جداً', color: 'bg-green-500' };
      default:
        return { strength: 0, text: 'ضعيفة جداً', color: 'bg-red-500' };
    }
  };

  const passwordStrength = getPasswordStrength(password || '');

  const onSubmit = async (data: TeacherRegisterForm) => {
    if (data.password !== data.confirmPassword) {
      toast.error('كلمات المرور غير متطابقة');
      return;
    }

    const selectedSpecializations = data.specialization || [];
    const selectedQualifications = data.qualifications || [];

    if (selectedSpecializations.length === 0) {
      toast.error('يرجى اختيار تخصص واحد على الأقل');
      return;
    }

    if (selectedQualifications.length === 0) {
      toast.error('يرجى اختيار مؤهل واحد على الأقل');
      return;
    }

    setLoading(true);

    try {
      const teacherData = {
        ...data,
        specialization: selectedSpecializations,
        qualifications: selectedQualifications,
        role: 'teacher' as const,
        subjects: selectedSpecializations, // إضافة subjects للتوافق
      };

      const result = await registerUser(teacherData);

      if (result.user && !result.error) {
        // يبدأ الاشتراك التجريبي من الخادم بعد توفر وثائق المدرس.
        try {
          const teacherId = result.user.id;
          await subscriptionService.getOrCreateSubscription(teacherId);
        } catch (subErr) {
          console.warn('Subscription setup deferred:', subErr);
        }
        toast.success('تم إنشاء حساب المدرس بنجاح.');
        toast.success('يمكنك الآن تسجيل الدخول والبدء بإدارة الطلاب');
        navigate('/login');
      } else {
        // معالجة أخطاء مفصلة
        const errorMessage = result.error || 'حدث خطأ في التسجيل';
        if (errorMessage.includes('email-already-in-use')) {
          toast.error(
            'هذا البريد الإلكتروني مستخدم بالفعل! يرجى استخدام بريد آخر أو تسجيل الدخول'
          );
        } else if (errorMessage.includes('weak-password')) {
          toast.error('كلمة المرور ضعيفة! يرجى استخدام كلمة مرور أقوى');
        } else if (errorMessage.includes('invalid-email')) {
          toast.error('البريد الإلكتروني غير صحيح! يرجى التحقق من صحة البريد');
        } else {
          toast.error(errorMessage);
        }
      }
    } catch (error: any) {
      console.error('Teacher registration error:', error);

      // معالجة أخطاء Firebase المفصلة
      let errorMessage = 'حدث خطأ في التسجيل';

      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'هذا البريد الإلكتروني مستخدم بالفعل.';
        toast.error(errorMessage);
        toast(
          'استخدم بريدًا إلكترونيًا آخر، أو انتقل لتسجيل الدخول إذا كان لديك حساب.'
        );
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'كلمة المرور ضعيفة جداً.';
        toast.error(errorMessage);
        toast(
          'يرجى استخدام كلمة مرور تحتوي على 8 أحرف على الأقل مع أرقام ورموز.'
        );
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'البريد الإلكتروني غير صحيح.';
        toast.error(errorMessage);
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'مشكلة في الاتصال بالإنترنت.';
        toast.error(errorMessage);
        toast('يرجى التحقق من اتصالك بالإنترنت والمحاولة مرة أخرى.');
      } else {
        toast.error(error.message || errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSpecialization = (spec: string) => {
    const current = getValues('specialization') || [];
    if (current.includes(spec)) {
      setValue(
        'specialization',
        current.filter(s => s !== spec),
        { shouldDirty: true, shouldValidate: true }
      );
    } else {
      setValue('specialization', [...current, spec], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const toggleQualification = (qual: string) => {
    const current = getValues('qualifications') || [];
    if (current.includes(qual)) {
      setValue(
        'qualifications',
        current.filter(q => q !== qual),
        { shouldDirty: true, shouldValidate: true }
      );
    } else {
      setValue('qualifications', [...current, qual], {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  const renderMultiPicker = ({
    name,
    label,
    options,
    selected,
    accent,
    onToggle,
  }: {
    name: PickerName;
    label: string;
    options: string[];
    selected: string[];
    accent: 'blue' | 'green';
    onToggle: (value: string) => void;
  }) => {
    const isOpen = openPicker === name;
    const accentClasses =
      accent === 'blue'
        ? {
            badge: 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200',
            active:
              'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-200',
            hover: 'hover:border-primary-300 dark:hover:border-primary-500',
            icon: 'text-primary-600 dark:text-primary-300',
          }
        : {
            badge: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-200',
            active:
              'border-green-500 bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-200',
            hover: 'hover:border-green-300 dark:hover:border-green-500',
            icon: 'text-green-600 dark:text-green-300',
          };

    return (
      <div className='form-group'>
        <label className='form-label'>{label} *</label>
        <button
          type='button'
          onClick={() => setOpenPicker(isOpen ? null : name)}
          className='flex min-h-[52px] w-full items-center justify-between gap-3 rounded-lg border border-gray-300 bg-white px-4 py-3 text-right transition-colors hover:border-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-white'
          aria-expanded={isOpen}
        >
          <span className='flex flex-1 flex-wrap items-center gap-2'>
            {selected.length > 0 ? (
              <>
                <span
                  className={`rounded-md px-2.5 py-1 text-xs font-semibold ${accentClasses.badge}`}
                >
                  {selected.length} محدد
                </span>
                {selected.slice(0, 3).map(item => (
                  <span
                    key={item}
                    className='rounded-md bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                  >
                    {item}
                  </span>
                ))}
                {selected.length > 3 && (
                  <span className='text-xs text-gray-500 dark:text-gray-400'>
                    +{selected.length - 3}
                  </span>
                )}
              </>
            ) : (
              <span className='text-sm text-gray-500 dark:text-gray-400'>
                اختر من القائمة
              </span>
            )}
          </span>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {isOpen && (
          <div className='mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/70'>
            <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'>
              {options.map(option => {
                const isSelected = selected.includes(option);
                return (
                  <button
                    key={option}
                    type='button'
                    onClick={() => onToggle(option)}
                    className={`flex min-h-[42px] items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                      isSelected
                        ? accentClasses.active
                        : `border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 ${accentClasses.hover}`
                    }`}
                  >
                    <span className='min-w-0 flex-1 text-right leading-5'>
                      {option}
                    </span>
                    {isSelected && (
                      <Check className={`h-4 w-4 shrink-0 ${accentClasses.icon}`} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageTransition>
      <div className='min-h-screen bg-brand-background dark:bg-gray-900'>
        <MobilePageHeader
          title='تسجيل مدرس جديد'
          subtitle='انضم إلى Class Tech وابدأ رحلتك التعليمية'
          onBack={() => navigate('/register')}
        />

        <MobileContainer className='py-6'>
          {/* Role Header */}
          <MobileCard className='mb-6 p-6'>
            <div className='flex items-center gap-4'>
              <div className='w-12 h-12 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center text-primary-600 dark:text-primary-300'>
                <School className='h-6 w-6' />
              </div>
              <div>
                <ClassTechLogo size='sm' className='mb-3' />
                <h2 className='text-lg font-semibold text-gray-900 dark:text-white'>
                  تسجيل مدرس جديد
                </h2>
                <p className='text-gray-600 dark:text-gray-400 text-sm'>
                  انضم إلينا كمدرس واكتسب طلاباً جدد
                </p>
              </div>
            </div>
          </MobileCard>

          <form onSubmit={handleSubmit(onSubmit)} className='space-y-6'>
            {/* البيانات الأساسية */}
            <MobileCard className='p-6'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-3'>
                البيانات الأساسية
              </h3>
              <div className='space-y-4'>
                <div className='form-group'>
                  <label className='form-label'>الاسم الكامل *</label>
                  <input
                    type='text'
                    className='input-field'
                    placeholder='أدخل اسمك الكامل'
                    {...register('name', {
                      required: 'الاسم مطلوب',
                      minLength: { value: 2, message: 'الاسم قصير جداً' },
                    })}
                  />
                  {errors.name && (
                    <p className='text-red-500 text-sm mt-1'>{errors.name.message as string}</p>
                  )}
                </div>

                <div className='form-group'>
                  <label className='form-label'>البريد الإلكتروني *</label>
                  <input
                    type='email'
                    className='input-field'
                    placeholder='teacher@example.com'
                    {...register('email', {
                      required: 'البريد الإلكتروني مطلوب',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'البريد الإلكتروني غير صحيح',
                      },
                    })}
                  />
                  {errors.email && (
                    <p className='text-red-500 text-sm mt-1'>{errors.email.message as string}</p>
                  )}
                </div>

                <div className='form-group'>
                  <label className='form-label'>رقم الهاتف *</label>
                  <input
                    type='tel'
                    className='input-field'
                    placeholder='+966501234567'
                    {...register('phone', {
                      required: 'رقم الهاتف مطلوب',
                      pattern: {
                        value: /^\+?[1-9]\d{1,14}$/,
                        message: 'رقم الهاتف غير صحيح',
                      },
                    })}
                  />
                  {errors.phone && (
                    <p className='text-red-500 text-sm mt-1'>{errors.phone.message as string}</p>
                  )}
                </div>
              </div>
            </MobileCard>

            {/* كلمة المرور */}
            <MobileCard className='p-6'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-3'>
                كلمة المرور
              </h3>
              <div className='space-y-4'>
                <div className='form-group'>
                  <label className='form-label'>كلمة المرور *</label>
                  <div className='relative'>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className='input-field pl-10'
                      placeholder='أدخل كلمة مرور قوية'
                      {...register('password', {
                        required: 'كلمة المرور مطلوبة',
                        minLength: { value: 8, message: 'كلمة المرور قصيرة جداً (8 أحرف على الأقل)' },
                      })}
                    />
                    <button
                      type='button'
                      className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className='h-5 w-5' />
                      ) : (
                        <Eye className='h-5 w-5' />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className='text-red-500 text-sm mt-1'>{errors.password.message as string}</p>
                  )}
                  {password && (
                    <div className='mt-2 space-y-1'>
                      <div className='flex justify-between text-xs'>
                        <span className='text-gray-500'>قوة كلمة المرور</span>
                        <span className={`font-medium ${passwordStrength.color.replace('bg-', 'text-')}`}>
                          {passwordStrength.text}
                        </span>
                      </div>
                      <div className='w-full bg-gray-200 rounded-full h-1.5'>
                        <div
                          className={`h-1.5 rounded-full transition-all ${passwordStrength.color}`}
                          style={{ width: `${passwordStrength.strength}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className='form-group'>
                  <label className='form-label'>تأكيد كلمة المرور *</label>
                  <div className='relative'>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      className='input-field pl-10'
                      placeholder='أعد إدخال كلمة المرور'
                      {...register('confirmPassword', {
                        required: 'تأكيد كلمة المرور مطلوب',
                        validate: value => value === password || 'كلمات المرور غير متطابقة',
                      })}
                    />
                    <button
                      type='button'
                      className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className='h-5 w-5' />
                      ) : (
                        <Eye className='h-5 w-5' />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className='text-red-500 text-sm mt-1'>{errors.confirmPassword.message as string}</p>
                  )}
                </div>
              </div>
            </MobileCard>

            {/* البيانات المهنية */}
            <MobileCard className='p-6'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-4 border-b border-gray-200 dark:border-gray-700 pb-3'>
                البيانات المهنية
              </h3>
              <div className='space-y-4'>
                <div className='form-group'>
                  <label className='form-label'>نبذة عنك *</label>
                  <textarea
                    className='input-field'
                    rows={3}
                    placeholder='اكتب نبذة مختصرة عن خبرتك وطريقة تدريسك...'
                    {...register('bio', {
                      required: 'النبذة مطلوبة',
                      maxLength: { value: 500, message: 'النبذة طويلة جداً' },
                    })}
                  />
                  {errors.bio && (
                    <p className='text-red-500 text-sm mt-1'>{errors.bio.message as string}</p>
                  )}
                </div>

                <div className='form-group'>
                  <label className='form-label'>سنوات الخبرة *</label>
                  <input
                    type='number'
                    className='input-field'
                    placeholder='5'
                    {...register('experience', {
                      required: 'سنوات الخبرة مطلوبة',
                      min: { value: 0, message: 'يجب أن تكون 0 أو أكثر' },
                      max: { value: 50, message: 'القيمة كبيرة جداً' },
                    })}
                  />
                  {errors.experience && (
                    <p className='text-red-500 text-sm mt-1'>{errors.experience.message as string}</p>
                  )}
                </div>

                <div className='form-group'>
                  <label className='form-label'>السعر في الساعة (جنيه مصري) *</label>
                  <input
                    type='number'
                    className='input-field'
                    placeholder='100'
                    {...register('pricePerHour', {
                      required: 'السعر مطلوب',
                      min: { value: 50, message: 'السعر يجب أن يكون 50 جنيهًا على الأقل' },
                      max: { value: 1000, message: 'السعر عالي جداً' },
                    })}
                  />
                  {errors.pricePerHour && (
                    <p className='text-red-500 text-sm mt-1'>{errors.pricePerHour.message as string}</p>
                  )}
                </div>
              </div>
            </MobileCard>

            {/* التخصصات والمؤهلات */}
            <MobileCard className='p-6'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-1 border-b border-gray-200 dark:border-gray-700 pb-3'>
                التخصصات والمؤهلات
              </h3>
              <div className='mt-4 space-y-4'>
                {renderMultiPicker({
                  name: 'specialization',
                  label: 'التخصصات',
                  options: specializations,
                  selected: selectedSpecializations,
                  accent: 'blue',
                  onToggle: toggleSpecialization,
                })}
                {renderMultiPicker({
                  name: 'qualifications',
                  label: 'المؤهلات العلمية',
                  options: qualificationOptions,
                  selected: selectedQualifications,
                  accent: 'green',
                  onToggle: toggleQualification,
                })}
              </div>
            </MobileCard>

            {/* خطة الاشتراك */}
            <MobileCard className='p-6'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white mb-1 border-b border-gray-200 dark:border-gray-700 pb-3'>
                اختر خطة الاشتراك
              </h3>
              <p className='text-sm text-gray-500 dark:text-gray-400 mb-4'>
                يمكنك تغيير الخطة في أي وقت من لوحة التحكم
              </p>
              <div className='space-y-3'>
                {REGISTRATION_PLANS.map(planId => {
                  const plan = PLAN_ENTITLEMENTS[planId];
                  const isSelected = selectedPlan === planId;
                  return (
                    <button
                      key={planId}
                      type='button'
                      onClick={() => setSelectedPlan(planId)}
                      className={`w-full p-4 rounded-xl border-2 text-right transition-all ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'
                      }`}
                    >
                      <div className='flex items-center justify-between mb-1'>
                        <div className='flex items-center gap-2'>
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                          }`}>
                            {isSelected && <div className='w-2 h-2 bg-white rounded-full' />}
                          </div>
                          <span className='text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-semibold'>
                            تجربة كاملة
                          </span>
                        </div>
                        <span className='font-bold text-gray-900 dark:text-white text-sm'>
                          مجانًا لمدة 14 يومًا
                        </span>
                      </div>
                      <p className='font-semibold text-gray-900 dark:text-white text-sm mb-0.5'>{plan.nameAr}</p>
                      <p className='text-xs text-gray-500 dark:text-gray-400'>
                        {plan.featuresAr.slice(0, 2).join(' • ')}
                      </p>
                    </button>
                  );
                })}
              </div>
              {selectedPlan !== 'trial' && (
                <p className='text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 mt-3'>
                  💳 ستحتاج إلى إدخال بيانات الدفع بعد التسجيل من صفحة إدارة الاشتراك
                </p>
              )}
            </MobileCard>

            {/* زر التسجيل */}
            <MobileCard className='p-6'>
              <div className='mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/70'>
                <label className='flex items-start gap-3 text-sm leading-7 text-gray-700 dark:text-gray-200'>
                  <input
                    type='checkbox'
                    className='mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500'
                    {...register('acceptTerms', {
                      required:
                        'يجب الموافقة على شروط الاستخدام وسياسة الخصوصية',
                    })}
                  />
                  <span>
                    أوافق على{' '}
                    <Link
                      to='/terms'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='font-semibold text-primary-600 hover:text-primary-800 dark:text-primary-300'
                    >
                      شروط الاستخدام
                    </Link>{' '}
                    و{' '}
                    <Link
                      to='/privacy'
                      target='_blank'
                      rel='noopener noreferrer'
                      className='font-semibold text-primary-600 hover:text-primary-800 dark:text-primary-300'
                    >
                      سياسة الخصوصية
                    </Link>{' '}
                    الخاصة بمنصة Class Tech.
                  </span>
                </label>
                {errors.acceptTerms && (
                  <p className='mt-2 text-sm text-red-500'>
                    {errors.acceptTerms.message as string}
                  </p>
                )}
              </div>
              <button
                type='submit'
                disabled={loading}
                className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700 shadow-sm'
                }`}
              >
                {loading ? (
                  <div className='flex items-center justify-center gap-2'>
                    <LoadingSpinner size='sm' />
                    <span>جاري إنشاء الحساب...</span>
                  </div>
                ) : (
                  'إنشاء حساب المدرس'
                )}
              </button>

              <div className='text-center mt-4'>
                <p className='text-gray-600 dark:text-gray-400 text-sm'>
                  لديك حساب بالفعل؟{' '}
                  <Link
                    to='/login'
                    className='text-primary-600 hover:text-primary-700 font-semibold transition-colors'
                  >
                    تسجيل الدخول
                  </Link>
                </p>
              </div>
            </MobileCard>
          </form>
        </MobileContainer>
      </div>
    </PageTransition>
  );
};

export default TeacherRegisterPage;
